import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList } from '../api/dataverseClient';
import { Task, Issue, Risk, Milestone } from '../types';
import { Decision } from './decisionService';
import { listProjects } from './projectService';

export async function searchProjectTasks(
  pca: IPublicClientApplication,
  projectId: string,
  query: string,
): Promise<Task[]> {
  const safe = query.replace(/'/g, "''");
  return dvList<Task>(pca, 'dagrun_pm_tasks', {
    select: [
      'dagrun_pm_taskid','dagrun_pm_name','dagrun_pm_status','dagrun_pm_priority',
      'dagrun_pm_assigneename','dagrun_pm_duedate','dagrun_pm_cardcolor',
      '_dagrun_pm_projectid_value','_dagrun_pm_wipcollectionid_value','_dagrun_pm_parenttaskid_value',
      'dagrun_pm_tasktype','dagrun_pm_assigneeid','dagrun_pm_startdate',
      'dagrun_pm_isoncriticalpath','dagrun_pm_dependencyslipflag','dagrun_pm_dependencyslipdays',
      'dagrun_pm_percentcomplete','dagrun_pm_estimatedhours','dagrun_pm_effortcompletedhours',
      'dagrun_pm_commentcount','dagrun_pm_subtaskcount','dagrun_pm_subtaskcompletedcount',
      'dagrun_pm_checklistcount','dagrun_pm_checklistcompletedcount','dagrun_pm_attachmentcount',
      'dagrun_pm_hasdependencies','dagrun_pm_hasaiactivity','dagrun_pm_isrecurringinstance',
    ],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and contains(dagrun_pm_name, '${safe}')`,
    top: 20,
  });
}

export async function searchProjectIssues(
  pca: IPublicClientApplication,
  projectId: string,
  query: string,
): Promise<Issue[]> {
  const safe = query.replace(/'/g, "''");
  return dvList<Issue>(pca, 'dagrun_pm_issues', {
    select: ['dagrun_pm_issueid', 'dagrun_pm_name', 'dagrun_pm_status', 'dagrun_pm_severity', '_dagrun_pm_projectid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and contains(dagrun_pm_name, '${safe}')`,
    top: 10,
  });
}

export async function searchProjectRisks(
  pca: IPublicClientApplication,
  projectId: string,
  query: string,
): Promise<Risk[]> {
  const safe = query.replace(/'/g, "''");
  return dvList<Risk>(pca, 'dagrun_pm_risks', {
    select: ['dagrun_pm_riskid', 'dagrun_pm_name', 'dagrun_pm_status', 'dagrun_pm_priorityscore', '_dagrun_pm_projectid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and contains(dagrun_pm_name, '${safe}')`,
    top: 10,
  });
}

export async function searchProjectDecisions(
  pca: IPublicClientApplication,
  projectId: string,
  query: string,
): Promise<Decision[]> {
  const safe = query.replace(/'/g, "''");
  return dvList<Decision>(pca, 'dagrun_pm_decisions', {
    select: ['dagrun_pm_decisionid', 'dagrun_pm_name', 'dagrun_pm_decisiondate', 'dagrun_pm_madebyname', '_dagrun_pm_projectid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and contains(dagrun_pm_name, '${safe}')`,
    top: 10,
  });
}

export async function searchProjectMilestones(
  pca: IPublicClientApplication,
  projectId: string,
  query: string,
): Promise<Milestone[]> {
  const safe = query.replace(/'/g, "''");
  return dvList<Milestone>(pca, 'dagrun_pm_milestones', {
    select: ['dagrun_pm_milestoneid', 'dagrun_pm_name', 'dagrun_pm_duedate', 'dagrun_pm_status', '_dagrun_pm_projectid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and contains(dagrun_pm_name, '${safe}')`,
    top: 10,
  });
}

export interface SearchResults {
  tasks: Task[];
  issues: Issue[];
  risks: Risk[];
  decisions: Decision[];
  milestones: Milestone[];
}

export async function searchProject(
  pca: IPublicClientApplication,
  projectId: string,
  query: string,
): Promise<SearchResults> {
  const [tasks, issues, risks, decisions, milestones] = await Promise.all([
    searchProjectTasks(pca, projectId, query).catch(() => []),
    searchProjectIssues(pca, projectId, query).catch(() => []),
    searchProjectRisks(pca, projectId, query).catch(() => []),
    searchProjectDecisions(pca, projectId, query).catch(() => []),
    searchProjectMilestones(pca, projectId, query).catch(() => []),
  ]);
  return { tasks, issues, risks, decisions, milestones };
}

// ── Cross-project search ──────────────────────────────────────────────
// One normalized hit shape across entity types. The project lookup's name
// is NOT directly $select-able (dagrun_pm_projectidname is a virtual
// FormattedValue attribute, not a property — selecting it 400s), so the
// project name is resolved client-side from a listProjects map in
// searchAllProjects. No project filter — row-level security trims results.

export interface SearchHit {
  kind: 'task' | 'issue' | 'decision' | 'milestone';
  id: string;
  name: string;
  projectId: string | null;
  projectName: string | null;
  status?: number | null;
  priority?: number | null;
  duedate?: string | null;
}

export interface CrossSearchResults {
  tasks: SearchHit[];
  issues: SearchHit[];
  decisions: SearchHit[];
  milestones: SearchHit[];
}

type Row = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

// When a projectId is supplied the search is scoped to that one project;
// otherwise it spans every project the caller can see (RLS trims results).
// The per-entity text predicate is wrapped in parentheses so the project
// scope AND doesn't bind only to the first OR branch.
function scopedFilter(projectId: string | undefined, predicate: string): string {
  return projectId ? `_dagrun_pm_projectid_value eq ${projectId} and (${predicate})` : predicate;
}

export async function searchAllTasks(pca: IPublicClientApplication, query: string, projectId?: string): Promise<SearchHit[]> {
  const safe = query.replace(/'/g, "''");
  const rows = await dvList<Row>(pca, 'dagrun_pm_tasks', {
    select: ['dagrun_pm_taskid', 'dagrun_pm_name', 'dagrun_pm_status', 'dagrun_pm_priority', '_dagrun_pm_projectid_value'],
    filter: scopedFilter(projectId, `contains(dagrun_pm_name, '${safe}') or contains(dagrun_pm_description, '${safe}')`),
    top: 10,
  });
  return rows.map(r => ({
    kind: 'task', id: String(r.dagrun_pm_taskid), name: str(r.dagrun_pm_name) ?? '(untitled)',
    projectId: str(r._dagrun_pm_projectid_value), projectName: null,
    status: num(r.dagrun_pm_status), priority: num(r.dagrun_pm_priority),
  }));
}

export async function searchAllIssues(pca: IPublicClientApplication, query: string, projectId?: string): Promise<SearchHit[]> {
  const safe = query.replace(/'/g, "''");
  const rows = await dvList<Row>(pca, 'dagrun_pm_issues', {
    select: ['dagrun_pm_issueid', 'dagrun_pm_name', 'dagrun_pm_status', '_dagrun_pm_projectid_value'],
    filter: scopedFilter(projectId, `contains(dagrun_pm_name, '${safe}')`),
    top: 5,
  });
  return rows.map(r => ({
    kind: 'issue', id: String(r.dagrun_pm_issueid), name: str(r.dagrun_pm_name) ?? '(untitled)',
    projectId: str(r._dagrun_pm_projectid_value), projectName: null,
    status: num(r.dagrun_pm_status),
  }));
}

export async function searchAllDecisions(pca: IPublicClientApplication, query: string, projectId?: string): Promise<SearchHit[]> {
  const safe = query.replace(/'/g, "''");
  // Decision body lives in dagrun_pm_decisiontext. Rationale is still
  // searched: it holds real content (the why), and the 167 pre-migration
  // records have their full text there until the data cleanup lands.
  const rows = await dvList<Row>(pca, 'dagrun_pm_decisions', {
    select: ['dagrun_pm_decisionid', 'dagrun_pm_name', '_dagrun_pm_projectid_value'],
    filter: scopedFilter(projectId, `contains(dagrun_pm_name, '${safe}') or contains(dagrun_pm_decisiontext, '${safe}') or contains(dagrun_pm_rationale, '${safe}')`),
    top: 5,
  });
  return rows.map(r => ({
    kind: 'decision', id: String(r.dagrun_pm_decisionid), name: str(r.dagrun_pm_name) ?? '(untitled)',
    projectId: str(r._dagrun_pm_projectid_value), projectName: null,
  }));
}

export async function searchAllMilestones(pca: IPublicClientApplication, query: string, projectId?: string): Promise<SearchHit[]> {
  const safe = query.replace(/'/g, "''");
  const rows = await dvList<Row>(pca, 'dagrun_pm_milestones', {
    select: ['dagrun_pm_milestoneid', 'dagrun_pm_name', 'dagrun_pm_status', 'dagrun_pm_duedate', '_dagrun_pm_projectid_value'],
    filter: scopedFilter(projectId, `contains(dagrun_pm_name, '${safe}')`),
    top: 5,
  });
  return rows.map(r => ({
    kind: 'milestone', id: String(r.dagrun_pm_milestoneid), name: str(r.dagrun_pm_name) ?? '(untitled)',
    projectId: str(r._dagrun_pm_projectid_value), projectName: null,
    status: num(r.dagrun_pm_status), duedate: str(r.dagrun_pm_duedate),
  }));
}

/**
 * Cross-entity search. When `projectId` is provided (the default from the
 * search UI) results are scoped to that one project; omit it for a
 * tenant-wide "search all projects" sweep.
 */
export async function searchAllProjects(pca: IPublicClientApplication, query: string, projectId?: string): Promise<CrossSearchResults> {
  const [tasks, issues, decisions, milestones, projects] = await Promise.all([
    searchAllTasks(pca, query, projectId).catch(() => [] as SearchHit[]),
    searchAllIssues(pca, query, projectId).catch(() => [] as SearchHit[]),
    searchAllDecisions(pca, query, projectId).catch(() => [] as SearchHit[]),
    searchAllMilestones(pca, query, projectId).catch(() => [] as SearchHit[]),
    listProjects(pca).catch(() => []),
  ]);
  const nameById = new Map(projects.map(p => [p.dagrun_pm_projectid, p.dagrun_pm_name]));
  const withNames = (hits: SearchHit[]) =>
    hits.map(h => ({ ...h, projectName: h.projectId ? (nameById.get(h.projectId) ?? null) : null }));
  return {
    tasks: withNames(tasks), issues: withNames(issues),
    decisions: withNames(decisions), milestones: withNames(milestones),
  };
}
